import React, { useState, useEffect } from "react";
import { Card, Typography, Flex, Avatar, Button, Select, Input, Tooltip, Space, message } from "antd";
import { PhoneOutlined, MailOutlined, StarFilled, HeartFilled, CopyOutlined, CheckOutlined, SendOutlined, HistoryOutlined, FileTextOutlined, CalculatorOutlined, PlusOutlined, CalendarOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import { WE_CRM_URL, WE_CRM_ACCESS } from "../../../config/api";
import { getCompanySettings } from "../../../services/organizationService";
import StartupDoctorModal from "../startup_doctor/StartupDoctorModal";
import { AVAILABLE_STATUSES } from "./leadsData";

const { Text } = Typography;

function LeadCardGrid({ leads, company, onSelectLead, onStatusChange, onAddRemark, onToggleStar, onToggleFavourite, onOpenHistory, onScheduleFollowup, availableStatuses, getAvailableStatusesForLead, onOpenMail, onOpenQuotation, onOpenProposal }) {
  const reduxCallStatus = useSelector((state) => state.executor?.companyInfo?.callStatus);
  let baseStatuses = AVAILABLE_STATUSES;
  if (Array.isArray(reduxCallStatus) && reduxCallStatus.length > 0) {
    baseStatuses = reduxCallStatus;
  } else if (Array.isArray(availableStatuses) && availableStatuses.length > 0) {
    baseStatuses = availableStatuses;
  }
  const statusList = baseStatuses.filter((st) => {
    const norm = (st || "").toLowerCase();
    return norm !== "closed lost" && norm !== "converted";
  });

  const [remarkInputs, setRemarkInputs] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  // WE-CRM / Startup Doctor Modal State
  const [showWeCrmModal, setShowWeCrmModal] = useState(false);
  const [selectedLeadForWeCrm, setSelectedLeadForWeCrm] = useState(null);
  const [weCrmUrl, setWeCrmUrl] = useState(WE_CRM_URL || "https://aistartupdoctor.com/api");
  const [weCrmCompanyId, setWeCrmCompanyId] = useState(null);
  const [weCrmAccessEnabled, setWeCrmAccessEnabled] = useState(false);

  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";
  const effectiveRole = user?.role || getCookie("role") || "";
  const isAdmin =
    effectiveRole === "admin" ||
    effectiveRole === "company_admin" ||
    effectiveRole === "super_admin";

  const targetWeCrmAccess = (process.env.REACT_APP_WE_CRM_ACCESS || WE_CRM_ACCESS || "").trim().toUpperCase();
  const hasWeCrmAccess =
    weCrmAccessEnabled ||
    (Boolean(targetWeCrmAccess) &&
      Boolean(effectiveCompanyCode) &&
      String(effectiveCompanyCode).trim().toUpperCase() === targetWeCrmAccess);

  useEffect(() => {
    if (!effectiveCompanyCode) return;
    getCompanySettings(effectiveCompanyCode)
      .then((res) => {
        if (res?.success && res.settings) {
          if (res.settings.weCrmUrl) setWeCrmUrl(res.settings.weCrmUrl);
          if (res.settings.weCrmCompanyId) setWeCrmCompanyId(res.settings.weCrmCompanyId);
          if (typeof res.settings.weCrmAccessEnabled === "boolean") {
            setWeCrmAccessEnabled(res.settings.weCrmAccessEnabled);
          }
        }
      })
      .catch((err) => console.warn("Could not load WE-CRM settings in LeadCardGrid:", err));
  }, [effectiveCompanyCode]);

  const handleCopy = (text, id) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedId(id);
        message.success("Copied to clipboard!");
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        message.error("Failed to copy to clipboard.");
      });
  };

  const handleRemarkSubmit = (leadId) => {
    const text = remarkInputs[leadId];
    if (!text?.trim()) return;
    onAddRemark(leadId, text.trim());
    setRemarkInputs((prev) => ({ ...prev, [leadId]: "" }));
  };

  const handleOpenWeCrm = (lead) => {
    const companyObj = company || {};
    const siblings = Array.isArray(leads) ? leads : [];
    const directors = siblings.map((l) => {
      const contactName = l.contactName || "";
      const nameParts = contactName.split(" ");
      const fName = l.directorFirstName || nameParts[0] || "";
      const lName = l.directorLastName || nameParts.slice(1).join(" ") || "";
      return {
        dealvoiceLeadId: l._id,
        firstName: fName,
        lastName: lName,
        fullName: (fName + " " + lName).trim(),
        email: l.directorEmailAddress || "",
        phone: String(l.contactNumber || ""),
        din: l.directorDin || "",
        role: l.designation || "Director",
      };
    });

    const address = [lead.streetAddressLine1 || companyObj.streetAddressLine1, lead.streetAddressLine2 || companyObj.streetAddressLine2, lead.city || companyObj.city, lead.state || companyObj.state, lead.postalCode || companyObj.postalCode].filter(Boolean).join(", ") || lead.address || companyObj.address || "";

    const weCrmData = {
      _leadId: lead._id,
      dealvoiceLeadId: lead._id,
      dealvoiceCompanyCode: lead.companyCode || companyObj.companyCode || "",
      dealvoiceLeadCode: lead.leadCode || companyObj.leadCode || "",
      owner_name: lead.contactName || lead.owner_name || "Client",
      company_name: lead.leadCompanyName || companyObj.companyName || companyObj.company_name || "",
      email: lead.directorEmailAddress || lead.companyEmail || lead.email || `client_${lead.contactNumber || lead.phone || "user"}@example.com`,
      company_email: lead.companyEmail || lead.directorEmailAddress || lead.email || "",
      phone: String(lead.contactNumber || lead.phone || ""),
      password: "",
      business_type: lead.companyType || companyObj.companyType || companyObj.business_type || "",
      pan: lead.pan || companyObj.pan || "",
      gstin: lead.gstNumber || companyObj.gstNumber || companyObj.gstin || "",
      address: address,
      cin: lead.cin || companyObj.cin || "",
      incorporation_date: lead.dateOfIncorporation || companyObj.dateOfIncorporation || companyObj.incorporation_date || "",
      roc: lead.roc || companyObj.roc || "",
      registration_number: lead.registrationNumber || companyObj.registrationNumber || companyObj.registration_number || "",
      company_origin: lead.companyOrigin || companyObj.companyOrigin || companyObj.company_origin || "Indian",
      class_of_company: lead.classOfCompany || companyObj.classOfCompany || companyObj.class_of_company || "Private",
      company_category: lead.companyCategory || companyObj.companyCategory || companyObj.company_category || "Company limited by shares",
      company_subcategory: lead.companySubcategory || companyObj.companySubcategory || companyObj.company_subcategory || "Non-government company",
      authorised_capital: lead.authorisedCapital || companyObj.authorisedCapital || companyObj.authorised_capital || "",
      paidup_capital: lead.paidUpCapital || companyObj.paidUpCapital || companyObj.paidup_capital || "",
      total_obligation_of_contribution: lead.totalObligationOfContribution || companyObj.totalObligationOfContribution || "",
      address_type: lead.addressType || companyObj.addressType || "Registered Address",
      main_division_no: lead.mainDivisionNo || companyObj.mainDivisionNo || "",
      street_address_line_1: lead.streetAddressLine1 || companyObj.streetAddressLine1 || "",
      street_address_line_2: lead.streetAddressLine2 || companyObj.streetAddressLine2 || "",
      city: lead.city || companyObj.city || "",
      state: lead.state || companyObj.state || "",
      postal_code: lead.postalCode || companyObj.postalCode || "",
      serviceName: "",
      isComplianceService: false,
      directors: directors,
    };

    setSelectedLeadForWeCrm(weCrmData);
    setShowWeCrmModal(true);
  };

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
          width: "100%",
        }}
      >
        {leads.map((lead) => {
          return (
            <Card
              key={lead._id}
              className="lead-grid-card"
              bordered={false}
              style={{
                borderRadius: 14,
                boxShadow: "none",
                border: "1px solid #f1f5f9",
                background: "#ffffff",
                overflow: "hidden",
              }}
              styles={{ body: { padding: 16 } }}
            >
              <Flex vertical gap={12}>
                {/* 1. Header: Avatar, Name, Designation, SPOC & Actions (Star, Heart, History) */}
                <Flex justify="space-between" align="start">
                  <Flex align="center" gap={10} style={{ minWidth: 0, flex: 1 }}>
                    <Avatar
                      size={38}
                      style={{
                        backgroundColor: "#2459d3",
                        flexShrink: 0,
                        fontWeight: 600,
                        fontSize: 15,
                      }}
                    >
                      {lead.contactName.charAt(0)}
                    </Avatar>
                    <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                      <Flex align="center" gap={6} wrap="nowrap">
                        <Text
                          strong
                          ellipsis
                          style={{
                            fontSize: "0.92rem",
                            color: "#0f172a",
                            maxWidth: 130,
                          }}
                          title={lead.contactName}
                        >
                          {lead.contactName}
                        </Text>
                        {lead.isStarred && (
                          <span className="spoc-badge" style={{ flexShrink: 0 }}>
                            SPOC
                          </span>
                        )}
                      </Flex>
                      <Text type="secondary" ellipsis style={{ fontSize: "0.76rem" }}>
                        {lead.designation || "Contact Person"}
                      </Text>
                    </Flex>
                  </Flex>

                  <Space size={2} style={{ flexShrink: 0 }}>
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <StarFilled
                          style={{
                            color: lead.isStarred ? "#eab308" : "#cbd5e1",
                            fontSize: 16,
                          }}
                        />
                      }
                      onClick={() => onToggleStar(lead._id)}
                      title="Point of Contact"
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={
                        <HeartFilled
                          style={{
                            color: lead.isFavourite ? "#ef4444" : "#cbd5e1",
                            fontSize: 16,
                          }}
                        />
                      }
                      onClick={() => onToggleFavourite(lead._id)}
                      title="Favourite"
                    />
                    <Tooltip title={`History (${lead.remarks?.length || 0})`}>
                      <Button
                        type="text"
                        size="small"
                        icon={
                          <HistoryOutlined
                            style={{
                              color: (lead.remarks?.length || 0) > 0 ? "#2459d3" : "#cbd5e1",
                              fontSize: 15,
                            }}
                          />
                        }
                        onClick={() => onOpenHistory(lead)}
                      />
                    </Tooltip>
                    <Tooltip title="Schedule Activity / Follow-up">
                      <Button
                        type="text"
                        size="small"
                        icon={<CalendarOutlined style={{ color: "#2459d3", fontSize: 15 }} />}
                        onClick={() => onScheduleFollowup?.(lead)}
                      />
                    </Tooltip>
                  </Space>
                </Flex>

                {/* 2. Contact Info Block */}
                <Flex
                  vertical
                  gap={6}
                  style={{
                    background: "#f8fafc",
                    padding: "8px 12px",
                    borderRadius: 8,
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap={8}>
                      <PhoneOutlined style={{ color: "#64748b", fontSize: 13 }} />
                      <Text style={{ fontSize: "0.82rem", fontWeight: 500 }}>{lead.contactNumber}</Text>
                    </Flex>
                    <Tooltip title="Copy phone number">
                      <Button type="text" size="small" icon={copiedId === `phone_${lead._id}` ? <CheckOutlined style={{ color: "#10b981" }} /> : <CopyOutlined style={{ color: "#94a3b8" }} />} onClick={() => handleCopy(lead.contactNumber, `phone_${lead._id}`)} />
                    </Tooltip>
                  </Flex>

                  {lead.directorEmailAddress && (
                    <Flex justify="space-between" align="center">
                      <Flex
                        align="center"
                        gap={8}
                        style={{ minWidth: 0, flex: 1, paddingRight: 6, cursor: onOpenMail ? "pointer" : "default" }}
                        onClick={() => onOpenMail?.(lead)}
                      >
                        <Tooltip title="Send Email">
                          <MailOutlined style={{ color: "#2563eb", fontSize: 13, flexShrink: 0 }} />
                        </Tooltip>
                        <Text ellipsis style={{ fontSize: "0.82rem", color: "#2563eb", textDecoration: "underline" }} title={lead.directorEmailAddress}>
                          {lead.directorEmailAddress}
                        </Text>
                      </Flex>
                      <Tooltip title="Copy email">
                        <Button type="text" size="small" icon={copiedId === `email_${lead._id}` ? <CheckOutlined style={{ color: "#10b981" }} /> : <CopyOutlined style={{ color: "#94a3b8" }} />} onClick={() => handleCopy(lead.directorEmailAddress, `email_${lead._id}`)} />
                      </Tooltip>
                    </Flex>
                  )}
                </Flex>

                {/* 3. Status Selector */}
                <Flex justify="space-between" align="center">
                  <Text type="secondary" style={{ fontSize: "0.78rem", fontWeight: 500 }}>
                    Status:
                  </Text>
                  <Select
                    size="small"
                    value={lead.status}
                    onChange={(val) => onStatusChange(lead._id, val)}
                    style={{ width: 130 }}
                    options={[
                      ...(getAvailableStatusesForLead ? getAvailableStatusesForLead(lead) : statusList).map((st) => ({
                        value: st,
                        label: st,
                      })),
                      {
                        value: "DIVIDER",
                        label: "──────",
                        disabled: true,
                      },
                      {
                        value: "ADD_DEAL",
                        label: "+ Add a Deal",
                      },
                    ]}
                  />
                </Flex>

                {/* 4. Inline Remark Box */}
                <Flex gap={6} align="center" style={{ width: "100%" }}>
                  <Input
                    size="middle"
                    placeholder="Type remark..."
                    value={remarkInputs[lead._id] || ""}
                    onChange={(e) => setRemarkInputs((prev) => ({ ...prev, [lead._id]: e.target.value }))}
                    onPressEnter={() => handleRemarkSubmit(lead._id)}
                    style={{ borderRadius: 8, fontSize: "0.82rem", flex: 1, minWidth: 0, height: 32 }}
                  />
                  <Button
                    shape="circle"
                    size="middle"
                    type="primary"
                    className="remark-send-btn"
                    icon={<SendOutlined style={{ fontSize: 13, marginLeft: -1, marginTop: 1 }} />}
                    disabled={!remarkInputs[lead._id]}
                    onClick={() => handleRemarkSubmit(lead._id)}
                  />
                </Flex>

                {/* 5. Footer Actions (Startup Doctor for Admin only, Quote, Proposal) */}
                <Flex
                  align="center"
                  gap={6}
                  style={{
                    paddingTop: 8,
                    borderTop: "1px solid #f1f5f9",
                    width: "100%",
                  }}
                >
                  {isAdmin && hasWeCrmAccess && (
                    <Button
                      size="small"
                      icon={<PlusOutlined style={{ fontSize: 11 }} />}
                      onClick={() => handleOpenWeCrm(lead)}
                      style={{
                        flex: 1.2,
                        minWidth: 0,
                        borderRadius: 6,
                        fontSize: "0.74rem",
                        fontWeight: 500,
                        padding: "0 6px",
                        height: 32,
                        color: "#2459d3",
                        borderColor: "#bfdbfe",
                        background: "#eff6ff",
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Startup Doctor
                    </Button>
                  )}

                  <Button
                    size="small"
                    icon={<CalculatorOutlined style={{ fontSize: 11 }} />}
                    onClick={() => {
                      if (onOpenQuotation) {
                        onOpenQuotation(lead);
                      } else {
                        message.info(`Opened Quotation for ${lead.contactName}`);
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 6,
                      fontSize: "0.74rem",
                      fontWeight: 500,
                      padding: "0 6px",
                      height: 32,
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    Quote
                  </Button>

                  <Button
                    size="small"
                    type="primary"
                    icon={<FileTextOutlined style={{ fontSize: 11 }} />}
                    onClick={() => {
                      if (onOpenProposal) {
                        onOpenProposal(lead);
                      } else {
                        message.info(`Opened Proposal for ${lead.contactName}`);
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 6,
                      fontSize: "0.74rem",
                      fontWeight: 500,
                      padding: "0 6px",
                      height: 32,
                      background: "#2459d3",
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    Proposal
                  </Button>
                </Flex>
              </Flex>
            </Card>
          );
        })}
      </div>

      {/* Startup Doctor Onboarding Modal */}
      <StartupDoctorModal
        visible={showWeCrmModal}
        onClose={() => {
          setShowWeCrmModal(false);
          setSelectedLeadForWeCrm(null);
        }}
        requestData={selectedLeadForWeCrm}
        weCrmUrl={weCrmUrl}
        weCrmCompanyId={weCrmCompanyId}
        onSuccess={() => {
          message.success("Successfully registered client in Startup Doctor!");
        }}
      />
    </>
  );
}

export default LeadCardGrid;
